import React, { useRef, useEffect, useCallback, RefObject } from 'react';

import { clsx } from 'clsx';

import { useLayoutContext } from '@ott/layout-context';

import FadeIn from 'src/components/elements/FadeIn/FadeIn';

import { S3_IMAGES_URL } from '../../constants';
import { HOTELS_CARDS_DATA } from './constants';

import { useInView } from 'framer-motion';

import styles from './BookStaysCards.scss';

const BookStaysCards = () => {
  const { isDesktop } = useLayoutContext();
  const ref: RefObject<HTMLDivElement> = useRef(null);
  const inViewport = useInView(ref);

  const threeDimensionalRotating = (event: MouseEvent): void => {
    const container = document.querySelector('#hotels-cards') as HTMLDivElement;
    const hotelImage = document.querySelectorAll('#hotel-image') as NodeListOf<HTMLImageElement>;

    // get mouse position
    const x = event.clientX;
    const y = event.clientY;

    // find the middle
    const middleX = window.innerWidth / 2;
    const middleY = window.innerHeight / 2;

    // get offset from middle
    const offsetX = ((x - middleX) / middleX) * 15;
    const offsetY = ((y - middleY) / middleY) * 15;

    container.style.setProperty('--rotateX', offsetY * -1 + 'deg');
    container.style.setProperty('--rotateY', offsetX + 'deg');

    hotelImage.forEach((image) => {
      image.style.setProperty('--translate3dX', offsetX * -1 + 'px');
      image.style.setProperty('--translate3dY', offsetY * -1 + 'px');
    });
  };

  useEffect(() => {
    document.addEventListener('mousemove', threeDimensionalRotating);

    if (!inViewport || !isDesktop) {
      document.removeEventListener('mousemove', threeDimensionalRotating);
    }

    return () => {
      document.removeEventListener('mousemove', threeDimensionalRotating);
    };
  }, [inViewport, isDesktop]);

  const verticalRotation = useCallback((): void => {
    const container = document.querySelector('#hotels-cards') as HTMLDivElement;
    const scrollY = window.scrollY / 10 - 75;

    if (scrollY > -35 && scrollY < 25) {
      container.style.setProperty('--rotateX', scrollY * -1 + 'deg');
    }
  }, []);

  useEffect(() => {
    if (!isDesktop) {
      document.addEventListener('scroll', verticalRotation);
    }

    return () => {
      document.removeEventListener('scroll', verticalRotation);
    };
  }, [isDesktop, verticalRotation]);

  return (
    <div className={styles.bookStaysCards}>
      <div className={styles.title}>
        <FadeIn delay={0.2}>
          <span>Book stays,</span>
          <br />
          <span className={styles.titleThin}>earn Solar Cash, fund your trips</span>
        </FadeIn>
      </div>
      <FadeIn className={styles.description} delay={0.4}>
        Get Solar Cash bonuses while booking your stays on Solartrip and redeem them on your next
        journeys
      </FadeIn>
      <FadeIn delay={0.6}>
        <div id="hotels-cards" className={styles.hotelsCards} ref={ref}>
          {HOTELS_CARDS_DATA.map((data) => (
            <div
              key={data.id}
              className={clsx(styles.hotelCard, styles[`card${data.id}`])}
              style={{
                width: data.width,
                height: data.height,
                borderRadius: data.style.borderRadius,
              }}
            >
              <img
                id="hotel-image"
                className={styles.hotelImage}
                style={{
                  width: data.width,
                  height: data.height,
                }}
                src={data.src}
                alt="hotel"
              />
              <img
                className={styles.heartIcon}
                style={{
                  ...data.style,
                  ...data.heartIconPosition,
                  borderRadius: 'none',
                }}
                src={`${S3_IMAGES_URL}book_stays_block/heart_icon.svg`}
                alt="heart"
              />
              <div className={styles.hotelRating} style={{ ...data.style, ...data.ratingPosition }}>
                <div className={styles.rating}>{data.rating}</div>
                <span className={styles.rewiews}>{data.reviews}</span>
              </div>
            </div>
          ))}
        </div>
      </FadeIn>
    </div>
  );
};

export default BookStaysCards;
